import { ChartColumnIcon, Skeleton, Text } from '@buffer-mono/popcorn'

import { useRecentMentionCount } from '../../hooks/mentions/useRecentMentionCount'
import type { MentionsEmptyStateChannel } from './types'

import styles from './MentionsStatsBar.module.css'

import type { JSX } from 'react'

const WINDOW_DAYS = 7

type MentionsStatsBarProps = {
  channels: MentionsEmptyStateChannel[]
}

/**
 * How many mentions these channels received recently, shown under the
 * caught-up empty state so a quiet inbox still says something about activity.
 *
 * Renders nothing when the count is unavailable: an empty inbox that also
 * reports "0 mentions" reads as a failure rather than a quiet week.
 */
export const MentionsStatsBar = ({
  channels,
}: MentionsStatsBarProps): JSX.Element | null => {
  const { count, isCapped, loading, error } = useRecentMentionCount({
    channels,
    days: WINDOW_DAYS,
    skip: channels.length === 0,
  })

  if (loading) {
    return <Skeleton height={42} />
  }

  if (error || count === null) {
    return null
  }

  return (
    <Text
      size="sm"
      color="default"
      className={styles.container}
      icon={<ChartColumnIcon size="small" color="subtle" />}
    >
      {isCapped ? `+${count}` : count} mention{count === 1 ? '' : 's'} in the
      last {WINDOW_DAYS} days
    </Text>
  )
}
